<template>
  <div>
    <DataTable
        class="mt-5"
        stripedRows
        :rowClass="getRowClass"
        :loading="isLoading"
        :value="currentTabState.tableData"
        :rows="currentTabState.rowsPerPage"
        :totalRecords="currentTabState.totalRecords"
        :sortField="currentTabState.sortField"
        :sortOrder="currentTabState.sortOrder"
        selectionMode="single"
        @row-select="onRowSelect"
        @row-click="onRowClick"
        @page="onPageChange"
        @sort="onSort"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="$rowsPerPageOptions"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        :lazy="true"
        :dataKey="dataKey"
        :expandedRows.sync="expandedRows"
        @row-expand="onRowExpand"
        @row-collapse="onRowCollapse"
    >
      <Column :expander="true" :headerStyle="{ width: '3rem' }" />

      <Column
          v-for="col in columns"
          :key="col.field"
          :field="col.field"
          :header="col.header"
          :sortable="col.sortable"
          :headerStyle="{ width: col.width }"
      >
        <template v-if="col.custom" #body="slotProps">
          <slot :name="col.slotName" :row-data="slotProps.data"/>
        </template>
      </Column>

      <Column v-if="showActionButton" header="" :headerStyle="{ width: '4rem', textAlign: 'center' }">
        <template #body="slotProps">
          <div class="flex justify-end">
            <AppButton
                color="blue"
                label=""
                :icon="true"
                :title="actionTooltip"
                @click.native.stop="$emit('action-click', slotProps.data)"
            >
              <template v-slot:icon>
                <Icon :iconType="actionIcon" />
              </template>
            </AppButton>
          </div>
        </template>
      </Column>

      <template #expansion="slotProps">
        <div class="nested-wrap">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-baseline gap-2">
              <div class="nested-title mb-0">EQUIPMENT</div>
              <span class="text-xs text-gray-500 italic">Select rows to mark as unoperational</span>
            </div>
            <div v-if="selectedEquipment.length > 0" class="flex items-center gap-2">
              <span class="text-xs text-secondary font-semibold">{{ selectedEquipment.length }} selected</span>
              <AppButton
                  color="red"
                  label="Make Unoperational"
                  @click.native.stop="onMakeUnoperationalClick"
              />
              <AppButton
                  color="gray"
                  label="Cancel"
                  @click.native.stop="clearSelection"
              />
            </div>
          </div>

          <DataTable
              class="nested-table"
              :value="slotProps.data.equipments"
              :selection.sync="selectedEquipment"
              dataKey="equipment_id"
              responsiveLayout="scroll"
              :showGridlines="false"
              :stripedRows="true"
          >
            <Column selectionMode="multiple" :headerStyle="{ width: '3rem' }" />
            <Column field="product_code" header="Code" :headerStyle="{ width: '16%' }" />
            <Column field="name" header="Description" :headerStyle="{ width: '22%' }" :bodyStyle="{ maxWidth: '0' }">
              <template #body="sp">
                <div class="truncate" :title="sp.data.name || ''">{{ sp.data.name || "-" }}</div>
              </template>
            </Column>
            <Column field="serial_number" header="Serial No." :headerStyle="{ width: '14%' }">
              <template #body="sp">
                {{ sp.data.serial_number || "-" }}
              </template>
            </Column>
            <Column field="make" header="Make" :headerStyle="{ width: '10%' }">
              <template #body="sp">
                {{ sp.data.make || "-" }}
              </template>
            </Column>
            <Column field="model_name" header="Model" :headerStyle="{ width: '10%' }">
              <template #body="sp">
                {{ sp.data.model_name || "-" }}
              </template>
            </Column>
            <Column field="service_agent" header="Service Agent" :headerStyle="{ width: '14%' }">
              <template #body="sp">
                {{ sp.data.service_agent || "-" }}
              </template>
            </Column>
            <Column v-if="expansionMode === 'equipment'" header="Expiry" :headerStyle="{ width: '14%' }">
              <template #body="sp">
                {{ resolveExpiry(sp.data) ? formatExpiry(resolveExpiry(sp.data)) : "-" }}
              </template>
            </Column>
            <Column v-else header="Job Type" :headerStyle="{ width: '14%' }">
              <template #body="sp">
                <span class="jobtype-pill">
                  {{ (sp.data.job_types && sp.data.job_types.length) ? sp.data.job_types.join(", ") : "-" }}
                </span>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>

      <template #empty>
        No records found.
      </template>
    </DataTable>

    <Paginator
        :first.sync="currentTabState.first"
        template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        @page="onPageChange"
        :rows.sync="currentTabState.rowsPerPage"
        :totalRecords="currentTabState.totalRecords"
        :rowsPerPageOptions="$rowsPerPageOptions"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";
import EquipmentService from "@/services/equipmentService";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  mixins: [notificationMixin],
  components: { Icon, AppButton },
  props: {
    columns: Array,
    enableRowClick: { type: Boolean, default: false },
    rowClass: { type: Function, default: () => () => '' },
    dataKey: { type: String, default: "schedule_id" },
    expansionMode: { type: String, default: "schedule" }, // "schedule" = Job Type column, "equipment" = Expiry column
    showActionButton: { type: Boolean, default: false },
    actionIcon: { type: String, default: "calendar-plus" },
    actionTooltip: { type: String, default: "Create Schedule" },
  },
  data() {
    return {
      expandedRows: [],
      selectedEquipment: [],
    };
  },
  computed: {
    ...mapState("schedule", {
      currentTabState: state => state.tabs[state.currentTab],
      isLoading: state => state.isLoading,
    }),
  },
  methods: {
    ...mapActions("schedule", ["updateTableSettings", "updateSortOptions"]),

    getRowClass(row) {
      return this.rowClass(row);
    },

    onRowSelect(event) {
      this.$emit("row-select", event);
    },
    onRowClick(event) {
      if (this.enableRowClick) {
        this.$emit("row-click", event);
      }
    },
    onPageChange(event) {
      this.expandedRows = [];
      this.updateTableSettings({
        rows: event.rows,
        page: event.page,
      });
    },
    onSort(event) {
      this.expandedRows = [];
      this.updateSortOptions({
        field: event.sortField,
        order: event.sortOrder,
      });
    },
    onRowExpand(event) {
      if (!event || !event.data) return;
      this.expandedRows = [event.data];
      this.selectedEquipment = [];
      this.$emit("row-expand", event);
    },
    onRowCollapse(event) {
      this.expandedRows = [];
      this.selectedEquipment = [];
      this.$emit("row-collapse", event);
    },
    clearSelection() {
      this.selectedEquipment = [];
    },
    async onMakeUnoperationalClick() {
      const ids = this.selectedEquipment
        .map(e => e && e.equipment_id)
        .filter(Boolean);
      if (!ids.length) return;
      const ok = await this.confirmAction({
        message: `Mark ${ids.length} equipment as unoperational? They will no longer appear in expiring lists until reactivated.`,
        header: 'Confirm Action',
        acceptLabel: 'Yes, Mark Unoperational',
        rejectLabel: 'No',
      });
      if (!ok) return;
      try {
        await EquipmentService.bulkUpdateOperationalStatus(ids, false);
        this.notifySuccess(`${ids.length} equipment marked unoperational.`);
        this.clearSelection();
        this.$emit('equipment-updated');
      } catch (error) {
        const msg = (error && error.data && error.data.message) || "Couldn't update equipment status.";
        this.notifyError(msg);
      }
    },
    formatExpiry(epoch) {
      if (!epoch) return "-";
      const m = this.$moment ? this.$moment.unix(epoch) : null;
      if (m && m.isValid()) return m.format("DD/MM/YYYY");
      return "-";
    },
    resolveExpiry(row) {
      if (!row) return null;
      const candidates = [
        row.nearest_expiry,
        row.latest_service_expiry,
        row.latest_calibration_expiry,
        row.expiry_date,
        row.calibration_with_max_expiry && row.calibration_with_max_expiry.cert_expiry_date,
      ].filter(v => typeof v === 'number' && v > 0);
      return candidates.length ? Math.min.apply(null, candidates) : null;
    },
  },
};
</script>

<style scoped>
::v-deep .p-datatable-tbody > tr.p-datatable-row-expansion > td {
  padding: 0 !important;
  background-color: #f2f4f6 !important;
  border: 0 !important;
}

.nested-wrap {
  border: 1px solid #c0c0c0;
  background: #fbfbfc;
  border-radius: 6px;
  padding: 12px 12px 6px 12px;
  margin: 8px 10px 10px 10px;
}

.nested-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #b45309;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid #f59e0b;
}

::v-deep .nested-table .p-datatable-thead > tr > th {
  background-color: #fff7ed !important;
  border-bottom: 1px solid #f3f4f6 !important;
}
::v-deep .nested-table .p-datatable-tbody > tr > td {
  background-color: transparent !important;
  border-color: #eef2f7 !important;
}
::v-deep .nested-table .p-datatable-thead > tr {
  border-bottom: 0 !important;
}
::v-deep .nested-table .p-datatable-tbody > tr:last-child,
::v-deep .nested-table .p-datatable-tbody > tr:last-child > td {
  border-bottom: 0 !important;
}

.jobtype-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #eef2ff;
  color: #3730a3;
}
</style>
